import { createReducer } from "@reduxjs/toolkit";
import type { DataManagementDataset } from "../../data/models/schemas";
import {
    analyzeDatasetSourceFile,
    createDataManagementDataset,
    createDataManagementSource,
    createDatasetAlias,
    createDatasetIngestionRun,
    createSdsIngestionRun,
    createSourceAlias,
    deleteDatasetAlias,
    deleteSourceAlias,
    getDataManagementDataset,
    getDataManagementSource,
    getDatasetIngestionRun,
    getDatasetIngestionRunProgress,
    getDatasetSourceRegistrationProgress,
    listDataManagementDatasets,
    listDataManagementIngestionRuns,
    listDataManagementSources,
    listDatasetAliases,
    listDatasetIngestionArtifacts,
    listDatasetIngestionMappings,
    listDatasetIngestionRunItems,
    listDatasetIngestionRuns,
    listDatasetSourceFiles,
    listDatasetSourceRegistrationItems,
    listSourceAliases,
    materializeSdsIngestionRun,
    parseDatasetIngestionRun,
    registerDatasetSourceFile,
    registerDatasetSourcePrefix,
    reprocessDatasetIngestionRun,
    retryDatasetIngestionRunItem,
    retryDatasetSourceRegistrationItem,
    deleteDataManagementDataset,
    updateDataManagementDataset,
    updateDataManagementSource,
    updateDatasetAlias,
    updateDatasetIngestionMapping,
    updateSourceAlias,
} from "../actions/dataManagement";
import type {
    DataManagementAlias,
    DataManagementRun,
    DataManagementSource,
    DataManagementState,
} from "../types/IDataManagementState";

type ActionMeta = {
    datasetId?: number;
    runId?: string;
    sourceId?: number;
    sourceFileId?: string;
    aliasId?: number;
};
const metaOf = (action: unknown): ActionMeta =>
    (action as { meta?: ActionMeta }).meta ?? {};

const defaultState = (): DataManagementState => ({
    datasets: [],
    datasetsById: {},
    datasetAliases: {},
    sources: [],
    sourcesById: {},
    sourceAliases: {},
    sourceFilesByDataset: {},
    sourceAnalysisById: {},
    runCatalog: { items: [], total: 0, page: 0, pageSize: 50 },
    runsByDataset: {},
    runsById: {},
    runProgressById: {},
    registrationProgressById: {},
    runItemsById: {},
    registrationItemsById: {},
    artifactsByRunId: {},
    mappingsByRunId: {},
    materializationByRunId: {},
    savedTimestamp: 0,
});

const upsertDataset = (
    state: DataManagementState,
    dataset: DataManagementDataset
) => {
    if (dataset.id == null) return;
    state.datasetsById[dataset.id] = dataset;
    const index = state.datasets.findIndex((item) => item.id === dataset.id);
    if (index >= 0) state.datasets[index] = dataset;
    else state.datasets.push(dataset);
};
const upsertSource = (
    state: DataManagementState,
    source: DataManagementSource
) => {
    if (source.id == null) return;
    const existing = state.sourcesById[source.id] ?? {};
    state.sourcesById[source.id] = { ...existing, source };
    const index = state.sources.findIndex((item) => item.id === source.id);
    if (index >= 0) state.sources[index] = source;
    else state.sources.push(source);
};
const indexRun = (state: DataManagementState, run: DataManagementRun) => {
    if (run.runId) state.runsById[run.runId] = run;
};
const upsertRun = (state: DataManagementState, run: DataManagementRun) => {
    indexRun(state, run);
    if (!run.runId || run.datasetId == null) return;
    const runs = state.runsByDataset[run.datasetId] ?? [];
    const index = runs.findIndex((item) => item.runId === run.runId);
    if (index >= 0) runs[index] = run;
    else runs.unshift(run);
    state.runsByDataset[run.datasetId] = runs;
};
const upsertAlias = (
    aliases: DataManagementAlias[],
    alias: DataManagementAlias
) => {
    const index = aliases.findIndex((item) => item.id === alias.id);
    if (index >= 0) aliases[index] = alias;
    else aliases.push(alias);
};

export const dataManagement = createReducer(defaultState(), (builder) => {
    builder.addCase(listDataManagementDatasets.success, (state, action) => {
        state.datasets = action.payload.items ?? [];
        state.datasets.forEach((dataset) => {
            if (dataset.id != null) state.datasetsById[dataset.id] = dataset;
        });
    });
    builder.addCase(getDataManagementDataset.success, (state, action) =>
        upsertDataset(state, action.payload)
    );
    builder.addCase(createDataManagementDataset.success, (state, action) => {
        upsertDataset(state, action.payload);
        state.savedTimestamp = Date.now();
    });
    builder.addCase(updateDataManagementDataset.success, (state, action) => {
        upsertDataset(state, action.payload);
        state.savedTimestamp = Date.now();
    });
    builder.addCase(deleteDataManagementDataset.success, (state, action) => {
        const { datasetId } = metaOf(action);
        if (datasetId == null) return;
        state.datasets = state.datasets.filter(
            (dataset) => dataset.id !== datasetId
        );
        delete state.datasetsById[datasetId];
        delete state.datasetAliases[datasetId];
        const sourceFileIds = (
            state.sourceFilesByDataset[datasetId]?.items ?? []
        )
            .map((file) => file.sourceFileId)
            .filter((sourceFileId): sourceFileId is string =>
                Boolean(sourceFileId)
            );
        sourceFileIds.forEach((sourceFileId) => {
            delete state.sourceAnalysisById[sourceFileId];
        });
        delete state.sourceFilesByDataset[datasetId];
        const runIds = new Set([
            ...(state.runsByDataset[datasetId] ?? []).flatMap((run) =>
                run.runId ? [run.runId] : []
            ),
            ...(state.runCatalog.items ?? [])
                .filter((run) => run.datasetId === datasetId)
                .flatMap((run) => (run.runId ? [run.runId] : [])),
            ...Object.values(state.runsById)
                .filter((run) => run.datasetId === datasetId)
                .flatMap((run) => (run.runId ? [run.runId] : [])),
        ]);
        delete state.runsByDataset[datasetId];
        state.runCatalog = {
            ...state.runCatalog,
            items: [],
            total: 0,
        };
        runIds.forEach((runId) => {
            delete state.runsById[runId];
            delete state.runProgressById[runId];
            delete state.registrationProgressById[runId];
            delete state.runItemsById[runId];
            delete state.registrationItemsById[runId];
            delete state.artifactsByRunId[runId];
            delete state.mappingsByRunId[runId];
            delete state.materializationByRunId[runId];
        });
        state.savedTimestamp = Date.now();
    });
    builder.addCase(listDatasetAliases.success, (state, action) => {
        const { datasetId } = metaOf(action);
        if (datasetId != null)
            state.datasetAliases[datasetId] = action.payload.items ?? [];
    });
    builder.addCase(createDatasetAlias.success, (state, action) => {
        if (action.payload.parentId != null) {
            const aliases = state.datasetAliases[action.payload.parentId] ?? [];
            upsertAlias(aliases, action.payload);
            state.datasetAliases[action.payload.parentId] = aliases;
        }
        state.savedTimestamp = Date.now();
    });
    builder.addCase(updateDatasetAlias.success, (state, action) => {
        if (action.payload.parentId != null) {
            const aliases = state.datasetAliases[action.payload.parentId] ?? [];
            upsertAlias(aliases, action.payload);
            state.datasetAliases[action.payload.parentId] = aliases;
        }
        state.savedTimestamp = Date.now();
    });
    builder.addCase(deleteDatasetAlias.success, (state, action) => {
        const { datasetId, aliasId } = metaOf(action);
        if (datasetId != null && aliasId != null) {
            state.datasetAliases[datasetId] = (
                state.datasetAliases[datasetId] ?? []
            ).filter((alias) => alias.id !== aliasId);
        }
        state.savedTimestamp = Date.now();
    });

    builder.addCase(listDataManagementSources.success, (state, action) => {
        state.sources = action.payload.items ?? [];
        state.sources.forEach((source) => upsertSource(state, source));
    });
    builder.addCase(getDataManagementSource.success, (state, action) => {
        const id = action.payload.source?.id;
        if (id != null) {
            state.sourcesById[id] = action.payload;
            if (action.payload.source)
                upsertSource(state, action.payload.source);
            state.sourceAliases[id] = action.payload.aliases ?? [];
        }
    });
    builder.addCase(createDataManagementSource.success, (state, action) => {
        upsertSource(state, action.payload);
        state.savedTimestamp = Date.now();
    });
    builder.addCase(updateDataManagementSource.success, (state, action) => {
        upsertSource(state, action.payload);
        state.savedTimestamp = Date.now();
    });
    builder.addCase(listSourceAliases.success, (state, action) => {
        const { sourceId } = metaOf(action);
        if (sourceId != null)
            state.sourceAliases[sourceId] = action.payload.items ?? [];
    });
    builder.addCase(createSourceAlias.success, (state, action) => {
        if (action.payload.parentId != null) {
            const aliases = state.sourceAliases[action.payload.parentId] ?? [];
            upsertAlias(aliases, action.payload);
            state.sourceAliases[action.payload.parentId] = aliases;
        }
        state.savedTimestamp = Date.now();
    });
    builder.addCase(updateSourceAlias.success, (state, action) => {
        if (action.payload.parentId != null) {
            const aliases = state.sourceAliases[action.payload.parentId] ?? [];
            upsertAlias(aliases, action.payload);
            state.sourceAliases[action.payload.parentId] = aliases;
        }
        state.savedTimestamp = Date.now();
    });
    builder.addCase(deleteSourceAlias.success, (state, action) => {
        const { sourceId, aliasId } = metaOf(action);
        if (sourceId != null && aliasId != null) {
            state.sourceAliases[sourceId] = (
                state.sourceAliases[sourceId] ?? []
            ).filter((alias) => alias.id !== aliasId);
        }
        state.savedTimestamp = Date.now();
    });

    builder.addCase(listDatasetSourceFiles.success, (state, action) => {
        const { datasetId } = metaOf(action);
        if (datasetId != null) {
            state.sourceFilesByDataset[datasetId] = action.payload;
        }
    });
    builder.addCase(registerDatasetSourceFile.success, (state, action) => {
        if (action.payload.datasetId != null) {
            const current = state.sourceFilesByDataset[
                action.payload.datasetId
            ] ?? { items: [] };
            current.items = [
                action.payload,
                ...(current.items ?? []).filter(
                    (item) => item.sourceFileId !== action.payload.sourceFileId
                ),
            ];
            state.sourceFilesByDataset[action.payload.datasetId] = current;
        }
    });
    builder.addCase(analyzeDatasetSourceFile.success, (state, action) => {
        const { sourceFileId } = metaOf(action);
        const id = action.payload.sourceFileId ?? sourceFileId;
        if (id) state.sourceAnalysisById[id] = action.payload;
    });

    builder.addCase(
        listDataManagementIngestionRuns.success,
        (state, action) => {
            state.runCatalog = action.payload;
            (action.payload.items ?? []).forEach((run) => indexRun(state, run));
        }
    );
    builder.addCase(listDatasetIngestionRuns.success, (state, action) => {
        const { datasetId } = metaOf(action);
        if (datasetId != null)
            state.runsByDataset[datasetId] = action.payload.items ?? [];
        (action.payload.items ?? []).forEach((run) => upsertRun(state, run));
    });
    builder.addCase(getDatasetIngestionRun.success, (state, action) =>
        upsertRun(state, action.payload)
    );
    builder.addCase(createDatasetIngestionRun.success, (state, action) =>
        upsertRun(state, action.payload)
    );
    builder.addCase(createSdsIngestionRun.success, (state, action) =>
        upsertRun(state, action.payload)
    );
    builder.addCase(registerDatasetSourcePrefix.success, (state, action) =>
        upsertRun(state, action.payload)
    );
    builder.addCase(reprocessDatasetIngestionRun.success, (state, action) =>
        upsertRun(state, action.payload)
    );
    builder.addCase(parseDatasetIngestionRun.success, (state, action) =>
        upsertRun(state, action.payload)
    );
    builder.addCase(getDatasetIngestionRunProgress.success, (state, action) => {
        const { runId } = metaOf(action);
        if (runId) state.runProgressById[runId] = action.payload;
    });
    builder.addCase(
        getDatasetSourceRegistrationProgress.success,
        (state, action) => {
            const { runId } = metaOf(action);
            if (runId) state.registrationProgressById[runId] = action.payload;
        }
    );
    builder.addCase(listDatasetIngestionRunItems.success, (state, action) => {
        const { runId } = metaOf(action);
        if (runId) state.runItemsById[runId] = action.payload;
    });
    builder.addCase(
        listDatasetSourceRegistrationItems.success,
        (state, action) => {
            const { runId } = metaOf(action);
            if (runId) state.registrationItemsById[runId] = action.payload;
        }
    );
    builder.addCase(retryDatasetIngestionRunItem.success, (state, action) => {
        const runId = action.payload.runId;
        if (!runId) return;
        const items = state.runItemsById[runId]?.items;
        const index =
            items?.findIndex((item) => item.itemId === action.payload.itemId) ??
            -1;
        if (items && index >= 0) items[index] = action.payload;
    });
    builder.addCase(
        retryDatasetSourceRegistrationItem.success,
        (state, action) => {
            const runId = action.payload.runId;
            if (!runId) return;
            const items = state.registrationItemsById[runId]?.items;
            const index =
                items?.findIndex(
                    (item) => item.itemId === action.payload.itemId
                ) ?? -1;
            if (items && index >= 0) items[index] = action.payload;
        }
    );
    builder.addCase(listDatasetIngestionArtifacts.success, (state, action) => {
        const { runId } = metaOf(action);
        if (runId) state.artifactsByRunId[runId] = action.payload;
    });
    builder.addCase(listDatasetIngestionMappings.success, (state, action) => {
        const { runId } = metaOf(action);
        if (runId) state.mappingsByRunId[runId] = action.payload.items ?? [];
    });
    builder.addCase(updateDatasetIngestionMapping.success, (state, action) => {
        const runId = action.payload.runId;
        if (!runId) return;
        const mappings = state.mappingsByRunId[runId] ?? [];
        const index = mappings.findIndex(
            (item) => item.decisionId === action.payload.decisionId
        );
        if (index >= 0) mappings[index] = action.payload;
        else mappings.push(action.payload);
        state.mappingsByRunId[runId] = mappings;
    });
    builder.addCase(materializeSdsIngestionRun.success, (state, action) => {
        const { runId } = metaOf(action);
        if (runId) state.materializationByRunId[runId] = action.payload;
    });
});
